import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api.client';
import { queryKeys } from '../lib/queryKeys';
import type { CaseDetail, CreateCaseDto } from '@lnr-v2/types';

interface UseCreateCaseReturn {
  createCase: (data: CreateCaseDto) => Promise<CaseDetail>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Custom hook for creating cases with React Query
 *
 * Benefits:
 * - Automatic cache invalidation after successful creation
 * - Consistent error handling
 * - Better integration with existing React Query setup
 * - Ensures cases list is refreshed when navigating after create
 */
export const useCreateCase = (): UseCreateCaseReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateCaseDto): Promise<CaseDetail> => {
      const response = await apiClient.post('cases', {
        json: data,
      });

      const result = await response.json<{
        success: boolean;
        data: CaseDetail;
      }>();

      if (result.success && result.data) {
        return result.data;
      }
      throw new Error('Failed to create case');
    },
    onSuccess: () => {
      // Invalidate all cases queries to trigger refetch
      // This ensures the cases list shows the newly created case
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
      });
    },
    onError: (err: unknown) => {
      // Error is already handled by React Query
      // But we ensure it's converted to Error type for consistency
      if (!(err instanceof Error)) {
        throw new Error('Failed to create case');
      }
    },
  });

  // Convert mutation error to Error type for backwards compatibility
  const error = mutation.error ? (mutation.error as Error) : null;

  return {
    createCase: mutation.mutateAsync,
    loading: mutation.isPending,
    error,
    reset: mutation.reset,
  };
};
